import { Badge } from "@bokio/designsystem/components/Badge/Badge";
import Icon from "@bokio/designsystem/components/Icon/Icon";
import { SG } from "@bokio/designsystem/components/SpacingGroup/SpacingGroup";
import { InfoTooltip } from "@bokio/designsystem/components/Tooltip";
import { Tooltip } from "@bokio/designsystem/components/Tooltip/Tooltip";
import Info from "@bokio/elements/Info/Info";
import { FieldRuleLevel } from "@bokio/shared/validation/entityValidator";
import { mergeClassNames } from "@bokio/utils/classes";

import { Validation } from "../Validation/Validation.internals";
import { Label } from "./Label";

import type { FontelloIcons } from "@bokio/assets/fontello";
import type { TooltipPlacement } from "@bokio/designsystem/components/Tooltip/tooltipUtils";
import type { RuleValidationResult } from "@bokio/shared/validation/entityValidator";
import type { ReactNode } from "react";

import * as styles from "./labelFor.scss";

export const getErrorsInOrder = (errors: RuleValidationResult[]) => {
	const mustFixNow = (errors && errors.filter(e => e.rule.level === FieldRuleLevel.MustFixNow)) || [];
	const canFixLater = (errors && errors.filter(e => e.rule.level === FieldRuleLevel.MustFixBeforeSend)) || [];

	return mustFixNow.concat(canFixLater);
};

export interface LabelTooltip {
	message: ReactNode;
	iconName?: FontelloIcons;
	trackinfo?: string;
	placement?: TooltipPlacement;
}

export interface LabelBadge {
	name: string;
	onClick: () => void;
}

interface LabelForProps {
	label: ReactNode;
	hint?: ReactNode;
	info?: ReactNode;
	children?: ReactNode;
	tooltip?: LabelTooltip;
	labelTooltip?: LabelTooltip;
	errors?: RuleValidationResult[];
	className?: string;
	testId?: string;
	htmlFor?: string;
	innerLabelClassName?: string;
	badge?: LabelBadge | LabelBadge[];
	/**
	 * Marks the field that the label is for as required by adding an asterisk (*) next to the label
	 */
	markAsRequired?: boolean;
}

const LabelFor = ({
	label,
	hint,
	children,
	errors,
	className,
	info,
	tooltip,
	labelTooltip,
	testId,
	htmlFor,
	innerLabelClassName,
	badge,
	markAsRequired = false,
}: LabelForProps) => (
	<label htmlFor={htmlFor} data-testid={testId} className={mergeClassNames(styles.label, className)}>
		{label && (
			<div className={styles.flex}>
				<Label label={label} hint={hint} className={innerLabelClassName} markAsRequired={markAsRequired} />
				{labelTooltip && (
					<InfoTooltip placement={labelTooltip.placement} contentGenerator={() => labelTooltip.message} />
				)}
			</div>
		)}
		{tooltip ? (
			<div className={styles.flex}>
				{children}
				{tooltip && (
					<Tooltip contentGenerator={() => tooltip.message} placement={tooltip.placement} trackinfo={tooltip.trackinfo}>
						<Icon className={styles.tooltipIcon} name={tooltip.iconName || "info"} size="24" color="grey" />
					</Tooltip>
				)}
			</div>
		) : (
			children
		)}
		{info && <Info>{info}</Info>}
		{badge && (
			<SG padding={[4, 0, 0]}>
				<SG horizontal wrap>
					{badge && !Array.isArray(badge) && <Badge color="ghost" name={badge.name} onClick={badge.onClick} />}
					{badge &&
						Array.isArray(badge) &&
						// eslint-disable-next-line @eslint-react/no-array-index-key
						badge.map(({ name, onClick }, index) => <Badge key={index} color="ghost" name={name} onClick={onClick} />)}
				</SG>
				<Validation testId={`Validation_Inline_${testId}`} errors={errors} />
			</SG>
		)}
		{!badge && <Validation testId={`Validation_Inline_${testId}`} errors={errors} />}
	</label>
);

export default LabelFor;
